import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import chatService, { ChatMessage, ChatRoom as ChatRoomType, MediaItem, formatParticipantName } from '../services/chatService';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'sonner';
import { MessageCircle, Send, ArrowLeft, Image as ImageIcon, Paperclip, SmilePlus, X, FileIcon } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useDropzone } from 'react-dropzone';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { createPortal } from 'react-dom';
import { formatTimeAgo, getUserTimezone } from '../utils/timeUtils';

// Add some global styles for emoji picker
import './emojiPickerStyles.css';

// Log user's timezone for debugging purposes
console.log('User timezone:', getUserTimezone());

const ChatMessageItem = memo(({ message, isCurrentUser }: { message: ChatMessage, isCurrentUser: boolean }) => {
  // Add a reference for object URLs to clean up when unmounted
  const objectUrls = useRef<string[]>([]);
  
  // Clean up object URLs when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      objectUrls.current.forEach(url => {
        // Only revoke blob URLs, not data URLs
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  return (
    <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} my-1 px-1`}>
      <div className={`max-w-[90%] sm:max-w-[85%] rounded-lg px-3 py-2 ${
        isCurrentUser 
          ? `bg-blue-500 text-white ${message.status === 'sending' ? 'opacity-70' : ''}` 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {message.content && (
          <p className="whitespace-pre-wrap break-words text-sm sm:text-base">{message.content}</p>
        )}
        
        {/* Display media items if present */}
        {message.media && message.media.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {message.media.map((item, index) => {
              // Track object URLs for cleanup - only for blob URLs
              if (item.url && item.url.startsWith('blob:')) {
                objectUrls.current.push(item.url);
              }
              
              // Create a unique key for each media item
              const mediaKey = `${message.id}-media-${index}`;
              
              return (
                <div key={mediaKey} className="relative">
                  {item.contentType?.startsWith('image/') ? (
                    <div className="block relative">
                      <img 
                        src={item.url} 
                        alt={item.fileName || 'Image'} 
                        className="max-h-44 max-w-full rounded-md object-contain"
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMzAgMzBINzBWNzBIMzBWMzBaIiBzdHJva2U9IiM5MDkwOTAiIHN0cm9rZS13aWR0aD0iMiIvPjxwYXRoIGQ9Ik00MCA0MEg0NVY0NUg0MFY0MFoiIGZpbGw9IiM5MDkwOTAiLz48cGF0aCBkPSJNMzAgNjBMNDUgNDVMNTUgNTVMNDUgNjVMMzAgNjBaIiBzdHJva2U9IiM5MDkwOTAiIHN0cm9rZS13aWR0aD0iMiIvPjxwYXRoIGQ9Ik01NSA1NUw2NSA0NUw3MCA1MFY3MEg0MFY2NUw0NSA2NUw1NSA1NVoiIHN0cm9rZT0iIzkwOTA5MCIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9zdmc+';
                          e.currentTarget.classList.add('bg-gray-100');
                          e.currentTarget.classList.add('p-2');
                        }}
                      />
                      {message.status === 'sending' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-md">
                          <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                  ) : item.contentType?.startsWith('video/') ? (
                    <div className="rounded-md overflow-hidden max-w-full">
                      {message.status === 'sending' ? (
                        <div className="relative bg-gray-800 max-h-44 max-w-full rounded-md flex items-center justify-center" style={{minHeight: '80px', minWidth: '120px'}}>
                          <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        </div>
                      ) : (
                        <video 
                          controls 
                          className="max-h-44 max-w-full" 
                          preload="metadata"
                        >
                          <source src={item.url} type={item.contentType} />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  ) : item.contentType?.startsWith('audio/') ? (
                    <div className="rounded-md overflow-hidden w-full max-w-xs">
                      {message.status === 'sending' ? (
                        <div className="bg-gray-200 p-2 rounded-md flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                          <span className="text-xs text-gray-700 truncate flex-1">{item.fileName || 'Audio file'}</span>
                          <div className="h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin ml-2"></div>
                        </div>
                      ) : (
                        <>
                          <audio 
                            controls 
                            className="w-full" 
                            preload="metadata"
                          >
                            <source src={item.url} type={item.contentType} />
                            Your browser does not support the audio tag.
                          </audio>
                          <div className="text-xs mt-1 overflow-hidden text-ellipsis">
                            {item.fileName || 'Audio file'}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <a 
                      href={message.status !== 'sending' ? item.url : undefined}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${isCurrentUser ? 'bg-blue-600' : 'bg-gray-200'} ${message.status === 'sending' ? 'pointer-events-none' : ''}`}
                    >
                      <FileIcon className={`h-4 w-4 mr-1 ${isCurrentUser ? 'text-blue-100' : 'text-gray-600'}`} />
                      <span className={`text-xs truncate max-w-[120px] ${isCurrentUser ? 'text-blue-50' : 'text-gray-800'}`}>
                        {item.fileName || 'File'}
                      </span>
                      {item.size && (
                        <span className={`text-xs ml-1 ${isCurrentUser ? 'text-blue-200' : 'text-gray-500'}`}>
                          ({Math.round(item.size / 1024)} KB)
                        </span>
                      )}
                      {message.status === 'sending' && (
                        <div className={`h-3 w-3 ml-1 rounded-full border-2 ${isCurrentUser ? 'border-blue-200' : 'border-gray-400'} border-t-transparent animate-spin`}></div>
                      )}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        <div className="flex items-center justify-between mt-1">
          <p className={`text-xs ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'}`}>
            {message.status === 'sending' 
              ? 'just now'
              : message.timestamp && !isNaN(new Date(message.timestamp).getTime()) 
                ? formatTimeAgo(message.timestamp)
                : 'Just now'}
          </p>
          
          {isCurrentUser && (
            <div className="ml-2">
              {message.status === 'sending' ? (
                <div className="h-3 w-3 rounded-full border-2 border-blue-100 border-t-transparent animate-spin"></div>
              ) : (
                <svg className="h-3 w-3 text-blue-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const ChatRoom: React.FC = () => {
  const { chatRoomId } = useParams<{ chatRoomId: string }>();
  const [chatRoom, setChatRoom] = useState<ChatRoomType | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedMedia, setUploadedMedia] = useState<MediaItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isAuthenticated, isLoading } = useAuth();
  const [userId] = useState(localStorage.getItem('userId'));
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [dragActive, setDragActive] = useState(false);

  // Move handleNewMessage outside of useEffect and declare it at component level
  const handleNewMessage = useCallback((message: ChatMessage) => {
    console.log('📩 New message received in component:', message);
    
    setMessages(prevMessages => {
      // First check if this exact message already exists by ID
      if (prevMessages.some(msg => msg.id === message.id)) {
        console.log('📩 Message already exists, not adding again:', message.id);
        return prevMessages;
      }
      
      // Check if this is a real message replacing a temp message
      // Improved matching logic to catch more cases
      const tempMessage = prevMessages.find(msg => 
        msg.id.startsWith('temp-') && 
        msg.senderId === message.senderId && 
        (
          msg.content === message.content ||
          // Also check if the content is similar (whitespace differences)
          msg.content.trim() === message.content.trim()
        )
      );

      if (tempMessage) {
        console.log('📩 Replacing temp message with real message:', message.id);
        return prevMessages.map(msg => 
          msg.id === tempMessage.id ? { ...message, status: 'sent' } : msg
        );
      }
      
      // Additional check for similar messages sent within last 5 seconds
      // This handles cases where the temp message logic fails
      const recentSameMessages = prevMessages.filter(msg => 
        msg.senderId === message.senderId && 
        msg.content === message.content &&
        msg.timestamp && 
        (new Date().getTime() - new Date(msg.timestamp).getTime() < 5000)
      );

      if (recentSameMessages.length > 0) {
        console.log('📩 Similar recent message found, not adding duplicate:', message.id);
        return prevMessages;
      }
      
      // If we get here, this is a completely new message
      console.log('📩 Adding new message to UI:', message.id);
      
      // Use setTimeout to ensure scrolling happens after the message is rendered
      setTimeout(() => scrollToBottom(), 50);
      
      return [...prevMessages, message];
    });
    
    // Mark messages as read after a short delay
    setTimeout(() => {
      if (chatRoomId) {
        chatService.markMessagesAsRead(chatRoomId);
      }
    }, 500);
  }, [chatRoomId]);

  useEffect(() => {
    if (!chatRoomId) return;

    const loadChatRoom = async () => {
      try {
        const room = await chatService.getChatRoomDetails(chatRoomId);
        
        // Debug logging for participant data
        console.log('Chat room loaded:', room);
        console.log('Participants:', room.participants);
        
        // Check if any participants have missing email data
        if (room.participants) {
          room.participants.forEach(participant => {
            if (!participant.email) {
              console.warn('Participant missing email:', participant);
            }
          });
        }
        
        setChatRoom(room);
      } catch (error) {
        console.error('Failed to load chat room:', error);
        toast.error('Could not load chat details');
        navigate('/chat');
      }
    };

    loadChatRoom();
  }, [chatRoomId, navigate]);

  useEffect(() => {
    if (!chatRoomId) return;

    const loadMessages = async () => {
      try {
        setLoading(true);
        const response = await chatService.getChatMessages(chatRoomId, 0, 20);
        
        const newMessages = response?.content || [];
        
        setMessages(newMessages.reverse());
        setHasMore(0 < (response?.totalPages - 1 || 0));
        setPage(0);
        
        chatService.markMessagesAsRead(chatRoomId);
      } catch (error) {
        console.error('Failed to load messages:', error);
      } finally {
        setLoading(false);
        // Add immediate scroll to bottom after loading messages
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
      }
    };

    loadMessages();
  }, [chatRoomId]);

  useEffect(() => {
    if (!chatRoomId) return;

    console.log('🔌 Setting up WebSocket for chat room:', chatRoomId);

    // Initialize WebSocket with a fresh token
    const token = localStorage.getItem('token') || '';
    chatService.initializeWebSocket(token, () => {
      console.log('🔌 WebSocket connected, subscribing to room:', chatRoomId);
      chatService.subscribeToRoom(chatRoomId, handleNewMessage);
      
      // Setup heartbeat to keep connection alive
      const heartbeatCleanup = chatService.setupHeartbeat();
      
      // Setup automatic reconnection
      const reconnectCleanup = chatService.setupReconnection();
      
      // Return cleanup function
      return () => {
        if (heartbeatCleanup) heartbeatCleanup();
        if (reconnectCleanup) reconnectCleanup();
      };
    });

    return () => {
      console.log('🔌 Unsubscribing from room:', chatRoomId);
      chatService.unsubscribeFromRoom(chatRoomId, handleNewMessage);
    };
  }, [chatRoomId, handleNewMessage]);

  useEffect(() => {
    if (messages.length > 0 && !loadingMore) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages, loadingMore]);

  const loadMoreMessages = async () => {
    if (!chatRoomId || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const result = await chatService.getChatMessages(chatRoomId, nextPage);
      
      const container = messagesContainerRef.current;
      const scrollHeight = container?.scrollHeight || 0;
      
      setMessages(prevMessages => [...result.content.reverse(), ...prevMessages]);
      setPage(nextPage);
      setHasMore(nextPage < result.totalPages - 1);
      
      if (container) {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - scrollHeight;
      }
    } catch (error) {
      console.error('Failed to load more messages:', error);
      toast.error('Failed to load older messages');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container && container.scrollTop === 0 && hasMore && !loadingMore) {
      loadMoreMessages();
    }
  };

  // Enhance file upload with drag and drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    // Filter out files that are too large (> 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = acceptedFiles.filter(file => file.size <= maxSize);
    const oversizedFiles = acceptedFiles.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      toast.error(`${oversizedFiles.length} file(s) exceeded the 10MB size limit and were skipped.`);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setDragActive(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
      'application/pdf': [],
      'video/*': [],
      'audio/*': []
    },
    maxSize: 10485760, // 10MB
    noClick: true, // Don't open file dialog on click
    noKeyboard: true // Don't open file dialog on keyboard
  });

  // Update dragActive state based on isDragActive from useDropzone
  useEffect(() => {
    setDragActive(isDragActive);
  }, [isDragActive]);

  // Modify the removeFile function to properly clean up resources
  const removeFile = (index: number) => {
    // Get the file to be removed
    const fileToRemove = selectedFiles[index];
    
    // Remove the file from the array
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Reset the file input to allow uploading the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Modify the code that creates temporary media objects to use data URLs instead of blob URLs
  const handleSendMessage = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if ((newMessage.trim() === '' && selectedFiles.length === 0) || sending || uploading) return;
  
    const messageContent = newMessage.trim();
    
    try {
      setSending(true);
      
      let mediaIds: string[] = [];
      
      // Create a temporary message object with properly prepared media previews
      const tempMedia = await Promise.all(selectedFiles.map(async (file, index) => {
        // Use data URLs instead of blob URLs to avoid CSP issues
        const dataUrl = await readFileAsDataURL(file);
        
        return {
          id: `temp-media-${index}`,
          url: dataUrl,
          contentType: file.type,
          fileName: file.name,
          size: file.size
        };
      }));
      
      // Create a temporary message object to show immediately
      const tempMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        chatRoomId,
        senderId: userId || '',
        content: messageContent,
        // Use explicit ISO string with Z to indicate UTC
        timestamp: new Date().toISOString(),
        read: false,
        status: 'sending',
        media: tempMedia
      };
      
      // Add the temporary message to the UI
      setMessages(prev => [...prev, tempMessage]);
      setNewMessage('');
      
      // Upload files if any are selected
      if (selectedFiles.length > 0) {
        try {
          setUploading(true);
          const uploadedFiles = await chatService.uploadMedia(selectedFiles);
          setUploadedMedia(uploadedFiles);
          mediaIds = uploadedFiles.map(file => file.id);
        } catch (error) {
          console.error('Failed to upload media:', error);
          toast.error('Failed to upload media');
          
          // Don't clear selected files on error, so user can retry
          setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
          setUploading(false);
          setSending(false);
          return;
        } finally {
          setUploading(false);
        }
      }
      
      // Clear selected files after adding them to the message
      setSelectedFiles([]);
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Send the message via WebSocket with mediaIds
      await chatService.sendMessageWebSocket(chatRoomId, messageContent, mediaIds);
      
      // Update the message status to sent
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === tempMessage.id ? { ...msg, status: 'sent' } : msg
        )
      );
      
      // Scroll to the bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  }, [chatRoomId, newMessage, selectedFiles, sending, uploading, userId]);

  // Add helper function to convert File to data URL
  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as data URL'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' && (e.shiftKey || e.ctrlKey))) {
      e.preventDefault();
      handleSendMessage(e as unknown as React.FormEvent);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  useEffect(() => {
    if (!chatRoomId || !isAuthenticated) return;
    
    // Check WebSocket status
    const status = chatService.checkWebSocketStatus();
    const shouldPoll = !status.connected;
    
    if (!shouldPoll) {
      console.log('🔄 WebSocket connected, using real-time updates');
      return;
    }
    
    console.log('🔄 Setting up polling as fallback');
    
    const interval = setInterval(async () => {
      try {
        const response = await chatService.getChatMessages(chatRoomId, 0, 20);
        
        if (response?.content && response.content.length > 0) {
          setMessages(prevMessages => {
            // Create a map of existing messages for faster lookup
            const existingMessages = new Map();
            prevMessages.forEach(msg => {
              if (!msg.id.startsWith('temp-')) {
                existingMessages.set(msg.id, true);
              }
            });
            
            // Filter out messages that already exist
            const newMessages = response.content
              .filter(newMsg => !existingMessages.has(newMsg.id))
              .reverse();
              
            if (newMessages.length > 0) {
              console.log('🔄 Polling found new messages:', newMessages.length);
              return [...prevMessages, ...newMessages].sort((a, b) => 
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              );
            }
            
            return prevMessages;
          });
        }
      } catch (error) {
        console.error('❌ Error polling for messages:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(interval);
  }, [chatRoomId, isAuthenticated]);

  useEffect(() => {
    // Check WebSocket status periodically
    const checkInterval = setInterval(() => {
      const status = chatService.checkWebSocketStatus();
      console.log('WebSocket status check:', status.connected ? 'Connected' : 'Disconnected');
      
      if (!status.connected) {
        console.log('WebSocket not connected, attempting to reconnect');
        const token = localStorage.getItem('token') || '';
        chatService.initializeWebSocket(token);
      }
    }, 10000);
    
    return () => clearInterval(checkInterval);
  }, []);

  // Add a useEffect to scroll to bottom when loading completes
  useEffect(() => {
    if (!loading && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 50);
    }
  }, [loading, messages.length]);

  // Handle clicks outside of emoji picker to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current && 
        !emojiPickerRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.emoji-toggle')
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showEmojiPicker]);

  const addEmoji = (emoji: any) => {
    // Insert emoji at current cursor position
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newText = 
        newMessage.substring(0, start) + 
        emoji.native + 
        newMessage.substring(end);
      
      setNewMessage(newText);
      
      // Focus back and set cursor position after emoji
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + emoji.native.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 10);
    } else {
      // Fallback if no selection reference is available
      setNewMessage(prev => prev + emoji.native);
    }
    
    // Don't close the picker immediately to allow for multiple emoji selection
  };

  // Update the emoji picker position when the button is clicked
  const handleEmojiButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If the picker is already showing, just hide it
    if (showEmojiPicker) {
      setShowEmojiPicker(false);
      return;
    }
    
    // Get the button's position
    const buttonRect = emojiButtonRef.current?.getBoundingClientRect();
    
    if (buttonRect) {
      // Calculate position to place the picker near the input area
      // Window-based positioning strategy is more reliable
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Mobile-first approach
      if (viewportWidth < 640) {
        // For mobile, position at the bottom center
        setPickerPosition({
          top: viewportHeight - 450, // Position from top
          left: (viewportWidth - 320) / 2, // Center horizontally
        });
      } else {
        // For desktop, position relative to the button
        // Place the picker above the input area
        const top = buttonRect.top - 420; // Above the button
        const left = Math.min(buttonRect.left, viewportWidth - 320 - 20);
        
        setPickerPosition({
          top: Math.max(10, top), // Keep at least 10px from top
          left: Math.max(10, left), // Keep at least 10px from left
        });
      }
      
      setShowEmojiPicker(true);
    } else {
      // Fallback - just toggle the picker
      setShowEmojiPicker(!showEmojiPicker);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
        <p className="text-gray-500">Loading conversation...</p>
      </div>
    );
  }

  if (!chatRoom) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <p className="text-gray-500">Conversation not found</p>
        <Button 
          variant="outline" 
          className="mt-4" 
          onClick={() => navigate('/chat')}
        >
          Back to Messages
        </Button>
      </div>
    );
  }

  const otherParticipant = chatRoom.participants?.find(p => p.id !== userId);

  return (
    <div 
      className="flex flex-col h-full bg-white overflow-hidden relative"
      {...getRootProps()}
    >
      {/* File dropzone overlay - appears when dragging files over the chat */}
      {dragActive && (
        <div className="absolute inset-0 bg-blue-500/30 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <div className="mx-auto h-12 w-12 mb-3 rounded-full bg-blue-100 flex items-center justify-center">
              <ImageIcon className="h-6 w-6 text-blue-500" />
            </div>
            <h3 className="text-base font-semibold mb-1">Drop your files here</h3>
            <p className="text-gray-500 text-xs">Upload images, videos, audio files or documents</p>
          </div>
        </div>
      )}
      
      <div className="border-b border-gray-200 p-2 sm:p-3 flex items-center bg-gradient-to-r from-blue-50 to-gray-50 sticky top-0 z-10">
        <button 
          onClick={() => navigate('/chat')}
          className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
          aria-label="Back to chat list"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        
        <Avatar className="h-10 w-10 ring-2 ring-blue-100">
          <AvatarImage 
            src={otherParticipant?.profilePicture} 
            alt={otherParticipant ? formatParticipantName(otherParticipant) : "Unknown User"} 
          />
          <AvatarFallback className="bg-blue-200 text-blue-700 font-semibold">
            {otherParticipant?.fullName && !otherParticipant.fullName.includes("null") ? 
              otherParticipant.fullName.charAt(0).toUpperCase() : 
              otherParticipant?.email ? otherParticipant.email.charAt(0).toUpperCase() : '?'}
          </AvatarFallback>
        </Avatar>
        
        <div className="ml-3 overflow-hidden">
          <h2 className="text-base font-semibold text-gray-900 truncate">
            {otherParticipant ? formatParticipantName(otherParticipant) : "Unknown User"}
          </h2>
          {otherParticipant?.email && (!otherParticipant.fullName || 
                                     otherParticipant.fullName.includes("null") || 
                                     formatParticipantName(otherParticipant) !== otherParticipant.email) && (
            <p className="text-xs text-gray-500 truncate">{otherParticipant.email}</p>
          )}
        </div>
      </div>
      
      <ScrollArea
        ref={messagesContainerRef}
        className="flex-1 p-2 sm:p-3"
        onScrollCapture={handleScroll}
      >
        {loadingMore && (
          <div className="text-center py-1">
            <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
            <span className="text-xs text-gray-500">Loading older messages...</span>
          </div>
        )}
        
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <MessageCircle className="h-10 w-10 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium text-sm">No messages yet</p>
            <p className="text-gray-400 text-xs mt-1">Start a conversation with {otherParticipant ? formatParticipantName(otherParticipant) : "this person"}!</p>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((message) => (
              <ChatMessageItem 
                key={message.id}
                message={message}
                isCurrentUser={message.senderId === userId}
              />
            ))}
            <div ref={messagesEndRef} className="h-1" />
          </div>
        )}
      </ScrollArea>
      
      <div className="border-t border-gray-200 p-2 sm:p-3 bg-gray-50">
        <form onSubmit={handleSendMessage} className="flex flex-col space-y-2">
          {/* Selected files preview - moved above the text input */}
          {selectedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedFiles.map((file, index) => {
                // Create a key that includes the file name and size to ensure uniqueness
                const fileKey = `${file.name}-${file.size}-${index}`;
                
                return (
                  <div key={fileKey} className="relative group">
                    <div className="border border-gray-200 rounded-md p-1 bg-gray-50 flex items-center">
                      {file.type.startsWith('image/') ? (
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={file.name} 
                          className="h-12 w-12 object-cover rounded"
                          onLoad={(e) => {
                            // Revoke the object URL after the image has loaded to free up memory
                            URL.revokeObjectURL((e.target as HTMLImageElement).src);
                          }}
                        />
                      ) : file.type.startsWith('video/') ? (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-200 rounded">
                          <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      ) : file.type.startsWith('audio/') ? (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-200 rounded">
                          <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                        </div>
                      ) : (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-200 rounded">
                          <Paperclip className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                      <div className="ml-2 max-w-[80px]">
                        <p className="text-xs font-medium truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button 
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="flex items-end space-x-2">
            <div className="flex-1 bg-white rounded-lg overflow-hidden border border-gray-200 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all relative">
              <Textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Message ${otherParticipant ? formatParticipantName(otherParticipant) : ''}...`}
                className="min-h-[40px] max-h-[100px] text-sm resize-none border-0 focus-visible:ring-0 shadow-none p-2"
                rows={1}
              />
              <div className="flex items-center px-2 py-1 border-t border-gray-100">
                <button 
                  type="button" 
                  ref={emojiButtonRef}
                  className="text-gray-400 hover:text-gray-600 mr-2 emoji-toggle"
                  onClick={handleEmojiButtonClick}
                >
                  <SmilePlus className="h-5 w-5" />
                </button>
                
                <button 
                  type="button" 
                  className="text-gray-400 hover:text-gray-600 mr-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                
                <input
                  {...getInputProps()}
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const filesArray = Array.from(e.target.files);
                      
                      // Filter out files that are too large
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      const validFiles = filesArray.filter(file => file.size <= maxSize);
                      const oversizedFiles = filesArray.filter(file => file.size > maxSize);
                      
                      if (oversizedFiles.length > 0) {
                        toast.error(`${oversizedFiles.length} file(s) exceeded the 10MB size limit and were skipped.`);
                      }
                      
                      if (validFiles.length > 0) {
                        setSelectedFiles(prev => [...prev, ...validFiles]);
                      }
                    }
                  }}
                  onClick={(e) => {
                    // Reset the value before opening to ensure onChange fires even if selecting the same file
                    e.currentTarget.value = '';
                  }}
                  accept="image/*,application/pdf,video/*,audio/*"
                />
                
                <div className="ml-auto">
                  <span className="text-xs text-gray-400 hidden sm:inline">Shift+Enter to send</span>
                </div>
              </div>
              
              {/* Emoji Picker */}
              {showEmojiPicker && createPortal(
                <div 
                  ref={emojiPickerRef} 
                  className="emoji-picker-absolute"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'fixed',
                    top: `${pickerPosition.top}px`,
                    left: `${pickerPosition.left}px`,
                    zIndex: 9999,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
                    width: '280px',
                  }}
                >
                  <div className="p-1">
                    <Picker 
                      data={data} 
                      onEmojiSelect={addEmoji}
                      theme="light"
                      previewPosition="none"
                      skinTonePosition="none"
                      searchPosition="top"
                      set="native"
                      navPosition="bottom"
                      perLine={7}
                      autoFocus={true}
                    />
                  </div>
                </div>,
                document.body
              )}
            </div>
            
            <Button
              type="submit"
              disabled={(newMessage.trim() === '' && selectedFiles.length === 0) || sending || uploading}
              className="bg-blue-500 hover:bg-blue-600 text-white h-9 w-9 p-0 rounded-full shrink-0 flex items-center justify-center"
              aria-label="Send message"
            >
              {uploading || sending ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatRoom;
